import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, Building2, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { submitLead, getGeoLocation, buildLeadPayload, getCountryCodeFromDialCode } from '../services/api';
import { validateEmail, validatePhone, validateName, validateRequired } from '../utils/validation';

const ContactUsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateField = (field, value) => {
    let validation = { isValid: true, error: '' };

    switch (field) {
      case 'name':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'phone':
        validation = validatePhone(value, formData.countryCode);
        break;
      case 'message':
        validation = validateRequired(value, 'Message', 1);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: validation.error
    }));

    return validation.isValid;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Validate on change if field was already touched
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      phone: true,
      message: true
    });

    // Validate all fields
    const nameValid = validateField('name', formData.name);
    const emailValid = validateField('email', formData.email);
    const phoneValid = validateField('phone', formData.phone);
    const messageValid = validateField('message', formData.message);

    if (!nameValid || !emailValid || !phoneValid || !messageValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form before submitting',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Fetch geolocation data
      const geoData = await getGeoLocation();
      
      // Extract numeric dial code from countryCode
      const dialCode = formData.countryCode.replace('+', '');

      const description = formData.message;

      // Build complete payload
      const payload = buildLeadPayload({
        name: formData.name,
        email: formData.email,
        countryDialCode: dialCode,
        phoneNumber: formData.phone,
        description: description
      }, geoData);

      // Submit lead
      const result = await submitLead(payload);

      if (result.success && result.statusCode === 200) {
        toast({
          title: 'Success!',
          description: result.message || 'Your message has been sent successfully.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          countryCode: '+1',
          message: ''
        });
        
        // Redirect to application page with customerId
        if (result.customerId) {
          navigate(`/application/main?customerid=${result.customerId}`);
        }
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Something went wrong. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900">
      <Navbar onOpenModal={() => setIsModalOpen(true)} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-100 mb-6">
              Get in{' '}
              <span className="bg-brand-gradient bg-clip-text text-transparent">
                Touch
              </span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Have questions about our services? We're here to help. Reach out to our expert team and we'll respond within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form Section */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div>
                  <h2 className="text-2xl font-bold text-gray-100 mb-6">Contact Information</h2>
                  <p className="text-gray-300 leading-relaxed mb-8">
                    Our team of experts is ready to assist you with all your trademark registration and business formation needs. Contact us through any of the following channels.
                  </p>
                </div>
            <div className="grid md:grid-cols-2 gap-12">
              
              {/* Contact Information */}
              <div className="space-y-8 animate-fade-in">
                

                {/* Contact Cards */}
                <div className="space-y-4">
                  {/* Address */}
                  <div className="bg-gradient-to-br from-navy-800 to-navy-700 rounded-xl p-6 hover:shadow-lg transition-all">
                    <div className="flex items-start space-x-4">
                      <div className="bg-brand-gradient p-3 rounded-lg">
                        <MapPin className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-100 mb-1">Our Office</h3>
                        <p className="text-gray-300">
                          1423 Broadway<br />
                          Oakland, CA 94612<br />
                          United States
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="bg-gradient-to-br from-navy-800 to-navy-700 rounded-xl p-6 hover:shadow-lg transition-all">
                    <div className="flex items-start space-x-4">
                      <div className="bg-brand-gradient p-3 rounded-lg">
                        <Phone className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-100 mb-1">Phone Number</h3>
                        <p className="text-gray-300">
                          Main: <a href="tel:+18446190702" className="text-brand-primary hover:underline">+1 (877) 676-4227</a>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="bg-gradient-to-br from-navy-800 to-navy-700 rounded-xl p-6 hover:shadow-lg transition-all">
                    <div className="flex items-start space-x-4">
                      <div className="bg-brand-gradient p-3 rounded-lg">
                        <Mail className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-100 mb-1">Email Address</h3>
                        <p className="text-gray-300">
                          General: <a href="mailto:info@trademarknexor.com" className="text-brand-primary hover:underline">info@trademarknexor.com</a>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Business Hours */}
                  <div className="bg-gradient-to-br from-navy-800 to-navy-700 rounded-xl p-6 hover:shadow-lg transition-all">
                    <div className="flex items-start space-x-4">
                      <div className="bg-brand-gradient p-3 rounded-lg">
                        <Clock className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-100 mb-1">Business Hours</h3>
                        <p className="text-gray-300">
                          Monday - Friday: 9:00 AM - 6:00 PM EST<br />
                          Saturday: 10:00 AM - 4:00 PM EST<br />
                          Sunday: Closed
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Chat Option */}
                <div className="bg-navy-700 border-2 border-navy-400/40 rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <MessageCircle className="text-brand-primary" size={28} />
                    <h3 className="font-bold text-gray-100 text-lg">Need Immediate Help?</h3>
                  </div>
                  <p className="text-gray-300 mb-4">
                    Our live chat support is available 24/7 to answer your questions instantly.
                  </p>
                  <Button onClick={(event) => {
                                  event.preventDefault();
                                  openZendeskChat();
                                }}
                    data-no-hover="true"
                    className="js-chat bg-brand-gradient text-white hover:opacity-90 w-full font-semibold"
                  >
                    Start Live Chat
                  </Button>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-navy-700 rounded-2xl shadow-xl p-8 animate-slide-up border border-navy-500/30" style={{height: 'fit-content'}}>
                <h2 className="text-2xl font-bold text-gray-100 mb-6">Send Us a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      onBlur={() => handleBlur('name')}
                      className={`py-5 ${errors.name && touched.name ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                    />
                    {errors.name && touched.name && (
                      <div className="flex items-center text-xs text-red-600 mt-1">
                        <AlertCircle size={12} className="mr-1" />
                        {errors.name}
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      className={`py-5 ${errors.email && touched.email ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                    />
                    {errors.email && touched.email && (
                      <div className="flex items-center text-xs text-red-600 mt-1">
                        <AlertCircle size={12} className="mr-1" />
                        {errors.email}
                      </div>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">
                      Contact Number *
                    </label>
                    <div className="flex gap-2">
                      <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
                        <SelectTrigger className="w-24 py-5 border-navy-500">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="+1">+1</SelectItem>
                          <SelectItem value="+44">+44</SelectItem>
                          <SelectItem value="+91">+91</SelectItem>
                          <SelectItem value="+92">+92</SelectItem>
                          <SelectItem value="+86">+86</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="tel"
                        placeholder="123-456-7890"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          handleChange('phone', value);
                        }}
                        onBlur={() => handleBlur('phone')}
                        maxLength={10}
                        className={`flex-1 py-5 ${errors.phone && touched.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                      />
                    </div>
                    {errors.phone && touched.phone && (
                      <div className="flex items-center text-xs text-red-600 mt-1">
                        <AlertCircle size={12} className="mr-1" />
                        {errors.phone}
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">
                      Your Message *
                    </label>
                    <Textarea
                      placeholder="Please describe how we can help you..."
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      onBlur={() => handleBlur('message')}
                      className={`min-h-[120px] resize-none ${errors.message && touched.message ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                    />
                    {errors.message && touched.message && (
                      <div className="flex items-center text-xs text-red-600 mt-1">
                        <AlertCircle size={12} className="mr-1" />
                        {errors.message}
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-brand-gradient text-white hover:opacity-90 py-5 text-lg font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {!isSubmitting && <Send className="mr-2" size={20} />}
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <h2 className="text-4xl font-bold text-gray-100 mb-4">
                Visit Our{' '}
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  Office
                </span>
              </h2>
              <p className="text-xl text-gray-300">
                We'd love to meet you in person. Schedule an appointment today.
              </p>
            </div>

            {/* Map Placeholder */}
            <div className="rounded-2xl overflow-hidden shadow-2xl animate-scale-in">
              <div className="relative h-96 rounded-lg overflow-hidden">
                <iframe
                  title="Google Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.086485647789!2d-122.41941548468134!3d37.77492977975942!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80858064dfad5b93%3A0x1234567890abcdef!2sSan%20Francisco!5e0!3m2!1sen!2sus!4v1710000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full"
                />
              </div>
              {/* <div className="relative h-96 bg-gray-200">
                <img
                  src="https://images.unsplash.com/photo-1758519288417-d359ac3c494d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMGNvbnN1bHRpbmclMjBvZmZpY2UlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzczOTQwMDY5fDA&ixlib=rb-4.1.0&q=85&w=1200"
                  alt="Office Location Map"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-brand-gradient opacity-20"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="bg-navy-700 rounded-full p-4 shadow-2xl">
                    <MapPin className="text-brand-primary" size={48} />
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact Us Section */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <h2 className="text-4xl font-bold text-gray-100 mb-4">
                Why Choose{' '}
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  Our Support
                </span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: CheckCircle,
                  title: '24-Hour Response Time',
                  desc: 'We respond to all inquiries within 24 hours, ensuring you get the help you need quickly.'
                },
                {
                  icon: Building2,
                  title: 'Expert Consultation',
                  desc: 'Our team of experienced professionals provides personalized guidance for your specific needs.'
                },
                {
                  icon: MessageCircle,
                  title: 'Multiple Contact Options',
                  desc: 'Reach us via phone, email, live chat, or visit our office - whatever works best for you.'
                }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="text-center p-6 bg-gradient-to-br from-navy-800 to-navy-700 rounded-xl hover:shadow-lg transition-all animate-scale-in" style={{animationDelay: `${idx * 0.1}s`}}>
                    <div className="bg-brand-gradient w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-100 mb-3">{item.title}</h3>
                    <p className="text-base text-gray-300 leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Don't wait any longer. Protect your brand and business today with our expert services.
            </p>
            <Button 
              size="lg"
              onClick={() => setIsModalOpen(true)}
              className="bg-navy-500 text-white hover:bg-navy-400 shadow-xl px-10 py-6 text-lg font-bold border-2 border-navy-400"
            >
              Start Your Application
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default ContactUsPage;
